/**
 * The residue structure card (`P18.1`, `P18.2`): hover or click a residue code, see the monomer.
 *
 * **Per residue, not per peptide** — the user's decision, 2026-08-27. A 16-mer drawn whole is one
 * dense picture in which "what is `X0067`" is a fragment the reader has to find; the alphabet has
 * 1,203 monomers and 92.8% of them never appear in this corpus (`V90`), so the question is almost
 * always about one of them.
 *
 * **One delegated listener over a container, not a listener per residue.** A sequence is 16 spans,
 * the detail pane rebuilds them on every selection, and a pane that attached 16 listeners per
 * click would leak one set per click for the life of the session.
 *
 * **Cached by code for the session.** A depiction is a build artefact keyed by the library
 * revision, so it cannot change while the page is open; re-fetching it on every hover would make
 * moving along a sequence sixteen requests.
 */
import { residue, type ResidueCardData } from '../api';

/** Codes that are tokens rather than monomers. They have no structure and never will. */
const NOT_A_RESIDUE = new Set(['<pad>', '<bos>', '<eos>', '<unk>']);

/** Placement, in CSS pixels (`V113`). `MIN_CARD_HEIGHT` is the floor a flip is judged against. */
const GAP = 6;
const MARGIN = 8;
const MIN_CARD_HEIGHT = 220;

/**
 * How long the card survives the pointer leaving the residue (`V113`).
 *
 * **Long enough to walk onto the card, and no longer.** The card is now scrollable and holds a
 * SMILES worth selecting, both of which need the pointer to reach it; a card that vanished on
 * `mouseout` could only ever be read by pinning it first, which is a gesture nobody discovers.
 * Entering the card cancels the timer, so the grace period is only ever paid while crossing the
 * gap between the two.
 */
const HIDE_DELAY_MS = 180;

export interface ResidueCards {
  /**
   * Delegate hover, focus and click on every `[data-residue]` inside `root`.
   *
   * **Returns its own detach** (`V112`). A detail pane lives as long as its pane does and never
   * needs it; the pinned map card is built fresh on every click, and a version of this that could
   * only add would leak one set of listeners per click for the life of the session.
   */
  attach(root: HTMLElement): () => void;
  /** Show the card for one code, positioned near `anchor`. Exported for the tests. */
  show(code: string, anchor: HTMLElement): void;
  hide(): void;
  destroy(): void;
}

/**
 * Split a sequence cell into per-residue spans (`P18.1`).
 *
 * Used by the detail pane for a stored sequence and by nothing else: a *generated* sequence is
 * also residue codes, and it also gets spans, because the question "what is this monomer" is the
 * same question whichever half of the pane it is asked in.
 *
 * `differing` marks the positions a trajectory diff found (`V131`, P27) and is optional: three of
 * the five call sites pass nothing and are unchanged. It may be **longer** than this sequence's own
 * code count — the diff runs over the longer of the two sequences — and the surplus is simply not
 * drawn here, because there is no span to mark. The caller states the length difference in words
 * instead; see `Step` in `trajectory.ts`.
 *
 * **A class, never an inline colour.** `.residue.differs` is styled with a tint and an underline
 * rather than a text colour, because the text colour of a sequence is P9.5's stored/generated
 * contract and must not be borrowed to mean something else — see `app.css`.
 */
export function residueSpans(
  sequence: string,
  className: string,
  differing?: readonly boolean[],
): HTMLElement {
  const node = document.createElement('p');
  node.className = className;
  const codes = sequence.split('-');
  codes.forEach((code, i) => {
    if (i > 0) node.append('-');
    const span = document.createElement('span');
    span.className = differing?.[i] === true ? 'residue differs' : 'residue';
    span.textContent = code;
    // Tokens are shown and are not interactive: leaving them out of the run would silently
    // shorten the sequence, and making them hoverable would promise a picture that cannot exist.
    if (!NOT_A_RESIDUE.has(code) && code !== '') {
      span.dataset.residue = code;
      span.tabIndex = 0;
    }
    node.append(span);
  });
  return node;
}

export function createResidueCards(): ResidueCards {
  const cache = new Map<string, Promise<ResidueCardData>>();
  const card = document.createElement('aside');
  card.className = 'residue-card';
  card.hidden = true;
  // **Attached on first use, not at import.** `main.ts` holds one of these in a module-level
  // constant, so appending here would make importing that module mutate the document — which is
  // true today only because `<script type="module">` is deferred and `document.body` happens to
  // exist. A module that reaches into the DOM to be *loaded* is a module that cannot be imported
  // by a test, and this one is.
  let attached = false;
  const ensureAttached = (): void => {
    if (attached) return;
    document.body.append(card);
    attached = true;
  };

  let open: string | null = null;
  let pinned = false;
  const roots: HTMLElement[] = [];

  const fetchCard = (code: string): Promise<ResidueCardData> => {
    let pending = cache.get(code);
    if (pending === undefined) {
      pending = residue(code);
      cache.set(code, pending);
    }
    return pending;
  };

  /**
   * Put the card where it fits, and cap it to what is on screen (`V113`, `P19.5`).
   *
   * **The page does not scroll** — `body` is `overflow: hidden` and this is `position: fixed` —
   * so anything below the fold is simply unreachable. The old two lines always placed the card
   * *below* the anchor, which meant a residue in the lower half of the panel opened a card whose
   * structure was cut off by the window edge with no way to reach it. Reported on
   * `ancestor_10m_kl003_residue_embedding`, where the sequence sits low in a tall detail pane.
   *
   * Three rules, in order:
   *
   * 1. **`max-height` is set to the room that side actually has**, and the card scrolls inside
   *    it. The card is then never taller than the viewport, whatever the depiction's aspect.
   * 2. **Below the anchor unless above has materially more room.** Below is where a hover card
   *    belongs — the pointer is already travelling downward — so flipping is a fallback rather
   *    than a preference.
   * 3. **Clamped to the viewport on both axes**, so a residue near the right edge does not open a
   *    card half off screen.
   *
   * Called twice per hover — once with the placeholder, once when the depiction has rendered —
   * because the height it measures is the height it has to place.
   */
  function place(anchor: HTMLElement): void {
    const box = anchor.getBoundingClientRect();
    const roomBelow = window.innerHeight - box.bottom - GAP - MARGIN;
    const roomAbove = box.top - GAP - MARGIN;
    // `scrollHeight` rather than `offsetHeight`: a card already capped by a previous `place` call
    // reports its clipped height, and the flip decision would then depend on where it was last.
    const wanted = card.scrollHeight;
    const above = roomBelow < Math.min(wanted, MIN_CARD_HEIGHT) && roomAbove > roomBelow;
    const room = Math.max(MIN_CARD_HEIGHT, above ? roomAbove : roomBelow);
    card.style.maxHeight = `${room}px`;
    const height = Math.min(card.offsetHeight, room);
    card.style.top = above
      ? `${Math.max(MARGIN, box.top - GAP - height)}px`
      : `${Math.min(box.bottom + GAP, window.innerHeight - height - MARGIN)}px`;
    card.style.left = `${Math.max(MARGIN, Math.min(box.left, window.innerWidth - card.offsetWidth - MARGIN))}px`;
  }

  function render(data: ResidueCardData): void {
    card.replaceChildren();
    const title = document.createElement('h3');
    title.textContent = data.code;
    const figure = document.createElement('div');
    figure.className = 'structure';
    // The SVG comes from our own build step and our own origin, never from a user or a corpus
    // cell, which is what makes assigning it as markup the right call rather than a hazard.
    figure.innerHTML = data.svg || '<p class="unavailable">no depiction — SMILES did not parse</p>';
    const facts = document.createElement('dl');
    for (const [key, value] of [
      ['class', data.backbone_class || '—'],
      ['source', data.code_source || '—'],
      ['SMILES', data.smiles || '—'],
    ] as const) {
      const dt = document.createElement('dt');
      dt.textContent = key;
      const dd = document.createElement('dd');
      dd.textContent = value;
      facts.append(dt, dd);
    }
    const label = document.createElement('p');
    label.className = 'monomer-label';
    label.textContent = data.label;
    card.append(title, figure, label, facts);
  }

  function show(code: string, anchor: HTMLElement): void {
    ensureAttached();
    cancelHide();
    open = code;
    card.hidden = false;
    card.replaceChildren(Object.assign(document.createElement('p'), { textContent: code }));
    place(anchor);
    void fetchCard(code)
      .then((data) => {
        // The pointer may have moved on while the request was in flight; a late response must not
        // replace a card the reader is now looking at.
        if (open !== code) return;
        render(data);
        place(anchor);
      })
      .catch((error: unknown) => {
        if (open !== code) return;
        card.replaceChildren(
          Object.assign(document.createElement('p'), {
            className: 'unavailable',
            textContent: error instanceof Error ? error.message : String(error),
          }),
        );
      });
  }

  function hide(): void {
    if (pinned) return;
    open = null;
    card.hidden = true;
  }

  let hideTimer: ReturnType<typeof setTimeout> | null = null;

  function cancelHide(): void {
    if (hideTimer !== null) clearTimeout(hideTimer);
    hideTimer = null;
  }

  /** Hide after `HIDE_DELAY_MS`, unless something cancels it first (`V113`). */
  function scheduleHide(): void {
    cancelHide();
    if (pinned) return;
    hideTimer = setTimeout(() => {
      hideTimer = null;
      hide();
    }, HIDE_DELAY_MS);
  }

  // The card keeps itself open while the pointer is on it. Attached once, at construction, rather
  // than per `show`: the element is reused for every residue.
  card.addEventListener('mouseenter', cancelHide);
  card.addEventListener('mouseleave', scheduleHide);

  const target = (event: Event): HTMLElement | null => {
    const node = (event.target as HTMLElement | null)?.closest?.('[data-residue]');
    return node instanceof HTMLElement ? node : null;
  };

  const onOver = (event: Event): void => {
    const node = target(event);
    if (node === null || pinned) return;
    show(node.dataset.residue as string, node);
  };
  const onOut = (event: Event): void => {
    if (target(event) !== null) scheduleHide();
  };
  const onClick = (event: Event): void => {
    const node = target(event);
    if (node === null) return;
    const code = node.dataset.residue as string;
    // A click pins the card so the SMILES can be selected and copied; a second click releases it.
    pinned = pinned && open === code ? false : true;
    if (pinned) show(code, node);
    else hide();
  };
  const onKey = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') {
      pinned = false;
      cancelHide();
      hide();
    }
  };

  const detach = (root: HTMLElement): void => {
    root.removeEventListener('mouseover', onOver);
    root.removeEventListener('mouseout', onOut);
    root.removeEventListener('focusin', onOver);
    root.removeEventListener('focusout', onOut);
    root.removeEventListener('click', onClick);
  };

  document.addEventListener('keydown', onKey);

  return {
    attach(root: HTMLElement): () => void {
      roots.push(root);
      root.addEventListener('mouseover', onOver);
      root.addEventListener('mouseout', onOut);
      root.addEventListener('focusin', onOver);
      root.addEventListener('focusout', onOut);
      root.addEventListener('click', onClick);
      return () => {
        const at = roots.indexOf(root);
        if (at >= 0) roots.splice(at, 1);
        detach(root);
      };
    },
    show,
    hide(): void {
      pinned = false;
      cancelHide();
      hide();
    },
    destroy(): void {
      document.removeEventListener('keydown', onKey);
      cancelHide();
      for (const root of roots) detach(root);
      roots.length = 0;
      card.remove();
    },
  };
}
